<script>
import { clone } from 'lodash-es';
import { __ } from '~/javascripts/lib/locale';
import StateSelect from '~/javascripts/forms/components/state_select.vue';
import CountrySelect from '~/javascripts/forms/components/country_select.vue';
import InputField from '~/javascripts/forms/components/input_field.vue';
import { COUNTRIES_WITH_STATES_REQUIRED } from '~/javascripts/constants';
import { FIELD_VALIDATIONS } from '~/javascripts/forms/validation/constants';
import { validateObjectPropRequiredFields } from '~/javascripts/lib/object_prop_validator';

const COMPONENT_NAME = 'BillingAccountAddressFields';

export default {
  name: COMPONENT_NAME,
  components: {
    CountrySelect,
    StateSelect,
    InputField,
  },
  props: {
    initialFormData: {
      type: Object,
      required: true,
      validator(initialFormData) {
        const requiredFields = ['address1', 'address2', 'country', 'state', 'city', 'zipCode'];
        return validateObjectPropRequiredFields(COMPONENT_NAME, initialFormData, {
          requiredFields,
        });
      },
    },
    showRequiredLabels: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['change'],
  data() {
    return {
      form: {
        address1: this.initialFormData.address1,
        address2: this.initialFormData.address2,
        country: this.initialFormData.country,
        state: this.initialFormData.state,
        city: this.initialFormData.city,
        zipCode: this.initialFormData.zipCode,
      },
    };
  },
  computed: {
    stateRequired() {
      return this.form.country in COUNTRIES_WITH_STATES_REQUIRED;
    },
    // Note that we pass this to `optional-text` to display the field as required/optional
    // `optional` and `optional-text` is not used for any validation purposes and is only used to display the label content
    // See more here: https://gitlab.com/gitlab-org/gitlab-ui/-/blob/8660f9fec8cb516908ea705c6a91d21c74895564/src/components/base/form/form_group/form_group.vue#L62-64
    stateOptionalText() {
      if (this.showRequiredLabels && this.stateRequired) {
        return __('(required)');
      }
      if (this.form.country && !this.stateRequired) {
        return __('(optional)');
      }

      return '';
    },
  },
  mounted() {
    // Triggering this on mount so that the validation can be run on the parent component
    // to determine the initial validation state.
    this.handleFormChange();
  },
  methods: {
    handleFormChange() {
      this.$emit('change', clone(this.form));
    },
    handleCountryChange(country) {
      if (country !== this.form.country) {
        this.form = {
          ...this.form,
          state: '',
          country,
        };
        this.handleFormChange();
      }
    },
    handleStateChange(state) {
      this.form = { ...this.form, state };
      this.handleFormChange();
    },
  },
  FIELD_VALIDATIONS,
  i18n: {
    labels: {
      country: 'Country',
      address: 'Address',
      city: 'City',
      state: 'State / Province',
      zipCode: 'ZIP / postal code',
    },
  },
};
</script>

<template>
  <div>
    <input-field
      id="address-one"
      v-model="form.address1"
      group-class="gl-mb-0"
      :label="__($options.i18n.labels.address)"
      :optional="showRequiredLabels"
      :optional-text="__('(required)')"
      test-id="address-one"
      rules="required|noEmoji"
      @input="handleFormChange"
    />

    <input-field
      id="address-two"
      v-model="form.address2"
      test-id="address-two"
      :label="__('Address line 2')"
      rules="noEmoji"
      :hide-label="true"
      @input="handleFormChange"
    />

    <div class="gl-grid gl-grid-cols-2 gl-gap-x-6">
      <country-select
        data-testid="country"
        :label="__($options.i18n.labels.country)"
        :initial-country="form.country"
        :optional="showRequiredLabels"
        :optional-text="__('(required)')"
        @change="handleCountryChange"
      />
      <state-select
        data-testid="state"
        :label="__($options.i18n.labels.state)"
        :country="form.country"
        :initial-state="form.state"
        :optional="true"
        :optional-text="stateOptionalText"
        @change="handleStateChange"
      />

      <input-field
        id="city"
        v-model="form.city"
        test-id="city"
        :rules="$options.FIELD_VALIDATIONS.city"
        :label="__($options.i18n.labels.city)"
        :optional="showRequiredLabels"
        :optional-text="__('(required)')"
        @input="handleFormChange"
      />

      <input-field
        id="postal-code"
        v-model="form.zipCode"
        test-id="postal-code"
        :rules="$options.FIELD_VALIDATIONS.postalCode"
        :label="__($options.i18n.labels.zipCode)"
        :optional="showRequiredLabels"
        :optional-text="__('(required)')"
        @input="handleFormChange"
      />
    </div>
  </div>
</template>
